import { VoiceApi, ApiError, type CostSummary, type CostPeriod } from './api';
import { CommandCapture } from './audio';
import { HandsFreeVoice, type HandsFreeMode } from './hands-free';
import { LiveWake } from './live-wake';
import { VoiceSession, type VoiceState } from './voice';
import { browserAdapters } from './adapters';
import { isExpired, isSlackPermalink } from './utils';
import { renderMarkdown } from './markdown';
import type { AppState, MeResponse, ActionConfirmation, BrowserAdapters } from './types';
import './style.css';

const GUIDANCE: Record<string, string> = {
  mic_denied: 'Microphone access was denied. Please allow microphone access in your browser settings and try again.',
  mic_not_found: 'No microphone was found. Connect a microphone or check your browser permissions.',
  network: 'Network connection lost. CM Voice needs an internet connection to work.',
  webrtc: 'The voice connection failed. Try ending the call and starting again.',
  session: 'The session could not start. Please try again.',
  conflict: 'An active voice session was found on another device. You can take it over or try again.',
  auth: 'Your session has expired. Please sign in again.',
  unknown_provider_outcome: 'Live voice is paused because a previous provider session could not be verified. Use typed commands and contact the pilot operator.',
  unknown: 'An unexpected error occurred. Please reload and try again.',
};

type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function h(
  tag: string,
  className?: string,
  attrs?: Record<string, string>,
  ...children: (Node | string)[]
): HTMLElement {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
  }
  for (const c of children) {
    el.append(c);
  }
  return el;
}

const ICONS: Record<string, string> = {
  mic: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Zm-7 9a7 7 0 0 0 14 0M12 18v4M8 22h8',
  micOff: 'm3 3 18 18M9 9v3a3 3 0 0 0 4.2 2.75M15 11.5V5a3 3 0 0 0-5.55-1.55M5 11a7 7 0 0 0 10.45 6.1M12 18v4M8 22h8',
  send: 'm22 2-7 20-4-9-9-4Z M22 2 11 13',
  volume: 'M11 5 6 9H2v6h4l5 4V5Zm4.5 4.5a5 5 0 0 1 0 5m2.5-7.5a9 9 0 0 1 0 10',
  volumeOff: 'm2 2 20 20M11 5 6 9H2v6h4l5 4v-6m4.5-3.5a5 5 0 0 1 .8 2.7m1.7-5.2a9 9 0 0 1 .9 8.2',
  chevronDown: 'm6 9 6 6 6-6',
  chevronUp: 'm18 15-6-6-6 6',
  plus: 'M12 5v14M5 12h14',
  search: 'm21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z',
  external: 'M14 3h7v7M21 3 10 14M19 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6',
};

function icon(name: keyof typeof ICONS, label?: string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'cm-icon');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', label ? 'false' : 'true');
  if (label) svg.setAttribute('aria-label', label);
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', ICONS[name]);
  svg.appendChild(path);
  return svg;
}


export class VoiceApp {
  private api: VoiceApi;
  private capture = new CommandCapture();
  private audioEnabled = false;
  private audioPhase: 'idle' | 'requesting' | 'recording' | 'transcribing' = 'idle';
  private audioMessage = '';
  private audioGeneration = 0;
  private audioAbort?: AbortController;
  private spokenAudio?: HTMLAudioElement;
  private spokenUrl?: string;
  private playbackBlocked = false;
  private session?: VoiceSession;
  private handsFree: HandsFreeVoice;
  private handsFreeMode: HandsFreeMode = 'off';
  private interactionMode: 'live' | 'ptt' = this.readInteractionMode();
  private resumeMenuOpen = false;
  private resumeQuery = '';
  private resumeConfirmation = '';
  private conversationStartedAtOverride?: string;
  private costDetailsOpen = false;

  private positionResumeSheet = (): void => {
    const maxSheetWidth = 480;
    if (!this.resumeMenuOpen || window.innerWidth > maxSheetWidth) {
      this.root.style.removeProperty('--vv-top');
      this.root.style.removeProperty('--vv-left');
      this.root.style.removeProperty('--vv-width');
      this.root.style.removeProperty('--vv-height');
      return;
    }
    const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    const top = vv ? Math.round(vv.offsetTop) : 0;
    const left = vv ? Math.round(vv.offsetLeft) : 0;
    const width = vv ? Math.round(vv.width) : window.innerWidth;
    const height = vv ? Math.round(vv.height) : window.innerHeight;
    this.root.style.setProperty('--vv-top', `${top}px`);
    this.root.style.setProperty('--vv-left', `${left}px`);
    this.root.style.setProperty('--vv-width', `${width}px`);
    this.root.style.setProperty('--vv-height', `${height}px`);
  };
  private installHelpOpen = false;
  private recordingStartedAt = 0;
  private recordingTimer?: number;
  private micLevel = 0;
  private micLevelUpdatedAt = 0;
  private takeoverOnWake = false;
  private state: AppState;
  private me?: MeResponse;
  private installEvent: InstallPromptEvent | null = null;
  private supportsVoice: boolean;
  private conversationId?: string;
  private conversations: Array<{ id: string; created_at?: string; updated_at: string }> = [];
  private root: HTMLElement;
  private headerEl: HTMLElement;
  private contentEl: HTMLElement;
  private statusBarEl: HTMLElement;
  private authGeneration = 0;
  private costRefreshAt = 0;
  private costRefreshBusy = false;
  private foregroundRefreshPromise?: Promise<void>;
  private commandAnswer = '';
  private commandBusy = false;
  private commandDraft = '';
  private commandRequest?: { text: string; selected_delegation_id?: string; request_id: string; timezone: string };
  private lastContentKey = '';
  private controlsEl?: HTMLElement;
  private captionsEl?: HTMLElement;
  private pendingEl?: HTMLElement;
  private delegationsEl?: HTMLElement;
  private selectedDelegationId?: string;
  private costEl?: HTMLElement;
  private commandUsage?: { input: number; output: number };
  private costs?: CostSummary;

  constructor(private container: HTMLElement, private adapters: BrowserAdapters = browserAdapters) {
    this.api = new VoiceApi(
      { fetch: adapters.fetch, WebSocket: adapters.WebSocket, location: adapters.location },
      ''
    );
    this.supportsVoice =
      typeof window.RTCPeerConnection === 'function' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function';
    this.state = {
      status: 'idle',
      authenticated: false,
      slackConnected: false,
      voiceEnabled: false,
      textEnabled: false,
      writeEnabled: false,
      captions: [],
      pending: [],
      delegations: [],
      usageSeconds: 0,
      costUsd: null,
      costFinal: false,
      muted: false,
      offline: !adapters.navigator.onLine,
      audioBlocked: false,
      takeoverOffered: false,
    };
    this.handsFree = new HandsFreeVoice({
      createWake: callbacks => new LiveWake(callbacks),
      foreground: () => !document.hidden && !this.state.offline && !!this.me?.authenticated && !!this.me.slack_connected && this.state.voiceEnabled,
      startLive: stream => this.startPaidCall(stream),
      stopLive: async () => { this.session?.setMuted(true); await this.session?.stop(); },
      onMode: mode => {
        this.handsFreeMode = mode;
        if (mode === 'muted') this.state.muted = true;
        else if (this.effectiveInteractionMode() === 'live') this.state.muted = false;
        if (this.root) this.render();
      },
      onError: message => { this.state.status = 'error'; this.state.error = {code:'session', message, recoverable:true}; this.render(); },
      onLevel: level => this.updateMicLevel(level),
    });
    const root = this.buildRoot();
    this.root = root;
    this.headerEl = root.querySelector('header') as HTMLElement;
    this.contentEl = root.querySelector('section') as HTMLElement;
    this.statusBarEl = root.querySelector('footer') as HTMLElement;
    container.textContent = '';
    container.appendChild(root);
    this.init();
  }

  private buildRoot(): HTMLElement {
    const main = h('main', 'cm-voice', { 'aria-live': 'polite' });
    const header = h('header', 'cm-header');
    const section = h('section', 'cm-content');
    const footer = h('footer', 'cm-status-bar');
    main.appendChild(header);
    main.appendChild(section);
    main.appendChild(footer);
    return main;
  }

  private readInteractionMode(): 'live' | 'ptt' {
    try {
      return window.localStorage.getItem('cm-voice-input-mode') === 'ptt' ? 'ptt' : 'live';
    } catch {
      return 'live';
    }
  }

  private setInteractionMode(mode: 'live' | 'ptt'): void {
    if (mode === 'live' && !this.state.voiceEnabled) return;
    if (mode === this.interactionMode) return;
    this.interactionMode = mode;
    try { window.localStorage.setItem('cm-voice-input-mode', mode); } catch { /* unavailable */ }
    if (mode === 'ptt') {
      void this.handsFree.stop();
    } else if (this.canArmLive() && !this.state.muted) {
      // This handler is the explicit user gesture that may request microphone
      // permission. Rendering or restoring the saved preference never does.
      void this.startCall();
    }
    this.render();
  }

  private effectiveInteractionMode(): 'live' | 'ptt' {
    return this.state.voiceEnabled ? this.interactionMode : 'ptt';
  }

  private async init() {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      const promptEvent = e as InstallPromptEvent;
      if (typeof promptEvent.prompt === 'function') this.installEvent = promptEvent;
      this.render();
    });
    window.addEventListener('appinstalled', () => {
      this.installEvent = null;
      this.installHelpOpen = false;
      this.render();
    });
    window.addEventListener('online', () => {
      this.state.offline = false;
      this.render();
    });
    window.addEventListener('offline', () => {
      this.state.offline = true;
      this.cancelAudio();
      void this.handsFree.stop();
      this.render();
    });
    window.addEventListener('focus', () => { void this.foregroundRefresh(); });
    window.addEventListener('beforeunload', () => { this.cancelAudio(); void this.handsFree.stop(); });
    window.addEventListener('pagehide', (event) => {
      if (!(event as PageTransitionEvent).persisted) this.cancelAudio();
    });
    window.addEventListener('pageshow', () => { void this.foregroundRefresh(); });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) void this.foregroundRefresh();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.resumeMenuOpen) {
        event.preventDefault();
        this.closeResumeMenu(true);
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (this.resumeMenuOpen && event.target instanceof Node && !this.root.querySelector('.cm-resume-menu')?.contains(event.target)) {
        this.closeResumeMenu(true);
      }
    });
    window.addEventListener('resize', this.positionResumeSheet);
    window.addEventListener('orientationchange', this.positionResumeSheet);
    const visualViewport = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener('resize', this.positionResumeSheet);
      visualViewport.addEventListener('scroll', this.positionResumeSheet);
    }
    await this.loadMe();
  }

  private foregroundRefresh(): Promise<void> {
    if (document.hidden) return Promise.resolve();
    if (!this.foregroundRefreshPromise) {
      this.foregroundRefreshPromise = this.loadMe().finally(() => {
        this.foregroundRefreshPromise = undefined;
      });
    }
    return this.foregroundRefreshPromise;
  }

  private async loadMe(withStatus = true) {
    const generation = this.authGeneration;
    try {
      const me = await this.api.me();
      if (generation !== this.authGeneration) return;
      if (this.state.authenticated && (!me.authenticated || this.state.user?.id !== me.user?.id || this.state.user?.team_id !== me.user?.team_id)) this.clearPrivateState();
      this.me = me;
      this.api.setCsrf(this.me.csrf_token ?? '');
      this.state.authenticated = this.me.authenticated ?? false;
      this.state.slackConnected = this.me.slack_connected ?? false;
      this.state.user = this.me.user;
      this.state.voiceEnabled = this.me.voice_enabled ?? false;
      this.audioEnabled = this.me.audio_enabled ?? false;
      if (!this.audioEnabled || !this.state.slackConnected) this.cancelAudio();
      if (!this.state.voiceEnabled || !this.state.slackConnected) void this.handsFree.stop();
      this.state.textEnabled = this.me.text_enabled ?? false;
      this.state.writeEnabled = this.me.write_enabled ?? false;
    } catch (e) {
      if (generation !== this.authGeneration) return;
      this.state.error = { code: 'network', message: 'Could not reach the server. Please check your connection.', recoverable: true };
    }
    this.render();
    if (withStatus && this.state.authenticated) {
      await this.loadStatus();
    }
  }

  private async loadStatus() {
    const generation = this.authGeneration;
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const cRes = await this.api.getConversations();
      if (generation !== this.authGeneration || !this.state.authenticated) return;
      this.conversations = Array.isArray(cRes.conversations) ? cRes.conversations : [];
      this.restoreConversationSelection();
      const conversationId = this.conversationId;
      const [dRes, uRes, aRes] = await Promise.all([this.api.getDelegations(), this.api.getUsage(conversationId, timezone), this.api.getPendingActions()]);
      if (generation !== this.authGeneration || !this.state.authenticated) return;
      // Cookies can change in another tab while this page retains an old
      // account's private view. Check the current principal before combining
      // status responses with that view, and discard the whole old generation.
      const me = await this.api.me();
      if (generation !== this.authGeneration || !this.state.authenticated) return;
      if (!me.authenticated || this.state.user?.id !== me.user?.id || this.state.user?.team_id !== me.user?.team_id) {
        this.clearPrivateState();
        await this.loadMe();
        return;
      }
      const restored = Array.isArray(aRes.actions) ? aRes.actions : [];
      const local = new Map(this.state.pending.map(action => [action.id, action]));
      this.state.pending = [...this.state.pending.filter(action => action.result || action.busy), ...restored.filter(action => !local.get(action.id)?.result && !local.get(action.id)?.busy).map(action => local.get(action.id) ?? action)];
      this.state.delegations = dRes.delegations as import('./types').Delegation[] ?? [];
      if (this.selectedDelegationId && !this.state.delegations.some(item => item.id === this.selectedDelegationId)) this.selectedDelegationId = undefined;
      this.state.usageSeconds = uRes.seconds ?? 0;
      this.state.costUsd = uRes.estimated_usd;
      this.state.costFinal = uRes.final ?? false;
      if (conversationId === this.conversationId) this.costs = uRes.costs;
      this.commandUsage = Number.isInteger(uRes.input_tokens) && Number.isInteger(uRes.output_tokens)
        ? { input: uRes.input_tokens!, output: uRes.output_tokens! } : undefined;
    } catch (err) {
      if (generation !== this.authGeneration) return;
      if (err instanceof ApiError && err.status === 401) { this.clearPrivateState(); return; }
      if (err instanceof ApiError && err.status === 403) {
        await this.accessUnavailable('Slack access is no longer available. Reconnect Slack or start a new conversation.');
        return;
      }
    }
    if (['idle', 'closed', 'error'].includes(this.state.status)) {
      this.render();
    } else {
      this.patchContent();
    }
  }

  private render() {
    this.updateHeader();
    this.updateContent();
    this.updateStatusBar();
  }

  private updateHeader() {
    this.headerEl.textContent = '';
    this.headerEl.appendChild(this.buildHeader());
  }

  private updateContent() {
    const key = this.contentKey();
    if (key !== this.lastContentKey) {
      this.lastContentKey = key;
      this.contentEl.textContent = '';
      this.buildContent();
    } else {
      this.patchContent();
    }
  }

  private updateStatusBar() {
    this.statusBarEl.textContent = '';
    if (this.state.authenticated && this.state.slackConnected) {
      this.costEl = this.buildCost();
      this.statusBarEl.appendChild(this.costEl);
    }
    const install = this.buildInstall();
    if (install.childElementCount) this.statusBarEl.appendChild(install);
  }

  private contentKey(): string {
    if (!this.state.authenticated) return 'auth';
    if (!this.state.slackConnected) return 'slack';
    if (this.handsFreeMode !== 'off') return 'call';
    if (['idle', 'closed', 'error'].includes(this.state.status)) return 'idle';
    return 'call';
  }

  private buildContent() {
    if (this.state.offline) this.contentEl.appendChild(this.buildOfflineBanner());
    if (this.state.error && this.state.status !== 'error') this.contentEl.appendChild(this.buildError());
    if (!this.state.authenticated) {
      this.contentEl.appendChild(this.buildSignIn());
      return;
    }
    if (!this.state.slackConnected) {
      this.contentEl.appendChild(this.buildConnectSlack());
      return;
    }
    if (this.handsFreeMode === 'off' && ['idle', 'closed', 'error'].includes(this.state.status)) {
      this.contentEl.appendChild(this.buildIdle());
      return;
    }
    this.contentEl.appendChild(this.buildCallPanel());
  }

  private patchContent() {
    if (this.contentKey() !== 'call') {
      this.contentEl.textContent = '';
      this.buildContent();
      return;
    }
    this.updateControls();
    this.updateCaptions();
    this.updatePending();
    this.updateDelegations();
    this.updateCost();
  }

  private buildHeader(): HTMLElement {
    const header = h('header', 'cm-header');
    const brand = h('div', 'cm-brand');
    const mark = h('span', 'cm-brand-mark');
    mark.appendChild(icon('mic'));
    brand.append(mark, h('h1', 'cm-title', {}, 'CM Voice'));
    header.appendChild(brand);
    if (this.state.user) {
      const info = h('div', 'cm-user-info');
      const name = h('span', 'cm-user-name');
      name.textContent = this.state.user.display_name;
      const status = h('span', 'cm-user-status');
      status.textContent = this.state.slackConnected ? '· Slack connected' : 'Slack not connected';
      const logout = h('button', 'cm-link-button cm-logout', { type: 'button' });
      logout.textContent = 'Log out';
      logout.addEventListener('click', () => this.logout());
      if (this.state.slackConnected) {
        info.append(h('span', 'cm-connected-dot'), name, status);
      } else {
        info.append(name, status);
        const reconnect = h('button', 'cm-link-button cm-reconnect', {type:'button'});
        reconnect.textContent = 'Reconnect Slack';
        reconnect.addEventListener('click', () => { this.adapters.location.href = '/auth/slack/connect'; });
        info.appendChild(reconnect);
      }
      info.appendChild(logout);
      header.appendChild(info);
    }
    return header;
  }

  private buildIdle(): HTMLElement {
    const wrap = h('div', 'cm-idle cm-workspace');
    if (!this.supportsVoice) wrap.appendChild(this.buildUnsupported());
    if (this.state.textEnabled) wrap.appendChild(this.buildConversations());
    const voiceCard = h('div', 'cm-voice-card');
    voiceCard.append(this.buildVoiceStatus());
    if (this.state.error && this.state.status === 'error') {
      const p = h('p', 'cm-status-copy');
      p.textContent = GUIDANCE[this.state.error.code] || this.state.error.message;
      voiceCard.appendChild(p);
      if (this.state.takeoverOffered) {
        const p2 = h('p');
        p2.textContent = 'Another active session was found. Taking over will end it and start a new one here.';
        voiceCard.appendChild(p2);
        const takeBtn = h('button', 'cm-button cm-button-primary', { type: 'button' });
        takeBtn.textContent = 'Take over';
        takeBtn.addEventListener('click', () => this.startCall(true));
        voiceCard.appendChild(takeBtn);
      }
      if (this.state.error.code !== 'unknown_provider_outcome') {
        const retryBtn = h('button', 'cm-button cm-button-primary', { type: 'button' });
        retryBtn.textContent = 'Retry';
        retryBtn.addEventListener('click', () => this.startCall());
        voiceCard.appendChild(retryBtn);
      }
    } else {
      const p = h('p', 'cm-status-copy');
      if (this.canArmLive() && this.effectiveInteractionMode() === 'live') {
        p.textContent = this.handsFreeMode === 'waiting'
          ? 'Just talk — your microphone stays local until speech begins.'
          : 'Choose Always listening to arm local speech detection.';
      } else {
        p.textContent = 'Tap to talk, then tap again when you are done. Posting still asks you to confirm.';
      }
      voiceCard.appendChild(p);
      if (this.canArmLive() && this.effectiveInteractionMode() === 'live' && this.handsFreeMode === 'off') {
        const btn = h('button', 'cm-button cm-button-primary', { type: 'button' });
        btn.textContent = 'Start';
        btn.addEventListener('click', () => this.startCall());
        voiceCard.appendChild(btn);
      }
    }
    voiceCard.appendChild(this.buildModeControl());
    if (this.audioEnabled && this.effectiveInteractionMode() === 'ptt') voiceCard.appendChild(this.buildRecordedAudio());
    voiceCard.appendChild(this.buildCommandForm());
    wrap.appendChild(voiceCard);
    wrap.appendChild(this.buildCaptions());
    wrap.appendChild(this.buildPending());
    this.delegationsEl = this.buildDelegations();
    wrap.appendChild(this.delegationsEl);
    return wrap;
  }

  private buildVoiceStatus(): HTMLElement {
    const row = h('div', 'cm-voice-status-row');
    const disc = h('span', `cm-voice-disc ${this.state.muted ? 'is-muted' : ''}`);
    if (this.state.muted) disc.appendChild(icon('micOff'));
    else if (this.spokenAudio) disc.appendChild(icon('volume'));
    else disc.appendChild(this.buildMicBars());
    const copy = h('div');
    const title = h('strong', 'cm-voice-title');
    const armed = ['arming', 'waiting', 'connecting', 'live'].includes(this.handsFreeMode);
    title.textContent = this.state.muted ? 'Muted'
      : this.spokenAudio ? 'Speaking'
      : this.effectiveInteractionMode() === 'ptt' ? 'Microphone off'
      : armed ? 'Listening' : 'Ready to listen';
    const subtitle = h('p', 'cm-voice-subtitle');
    subtitle.textContent = this.spokenAudio ? 'Still listening — interrupt any time.'
      : this.state.muted ? 'Microphone off.'
      : this.effectiveInteractionMode() === 'ptt' ? 'Tap to talk when you are ready.'
      : armed ? 'Just talk. Live starts only after local speech.'
      : 'Choose Always listening to arm local speech detection.';
    copy.append(title, subtitle);
    const actions = h('div', 'cm-voice-actions');
    if (this.spokenAudio || this.playbackBlocked) {
      const stop = h('button', 'cm-button cm-stop-speaking', { type: 'button' });
      stop.append(icon('volumeOff'), 'Stop');
      stop.addEventListener('click', () => { this.stopPlayback(); this.render(); });
      actions.appendChild(stop);
    }
    const mute = h('button', 'cm-button cm-mute cm-record-mute cm-icon-button', { type: 'button', 'aria-label': this.state.muted ? 'Unmute microphone' : 'Mute microphone', 'aria-pressed': String(this.state.muted) });
    mute.append(
      icon(this.state.muted ? 'mic' : 'micOff'),
      h('span', 'cm-sr-only', {}, this.state.muted ? 'Unmute' : 'Mute'),
    );
    mute.addEventListener('click', () => this.setMuted(!this.state.muted));
    actions.appendChild(mute);
    row.append(disc, copy, actions);
    return row;
  }

  private buildMicBars(): HTMLElement {
    const bars = h('span', `cm-mic-bars ${this.audioPhase === 'recording' || this.handsFreeMode === 'waiting' ? 'is-active' : ''}`, { 'aria-hidden': 'true' });
    bars.style.setProperty('--cm-level', String(this.micLevel));
    for (let index = 0; index < 5; index += 1) bars.appendChild(h('i'));
    return bars;
  }

  private buildModeControl(): HTMLElement {
    const row = h('div', 'cm-mode-row');
    const segmented = h('div', 'cm-segmented', { role: 'group', 'aria-label': 'Voice input mode' });
    const modes: Array<'live' | 'ptt'> = this.state.voiceEnabled ? ['live', 'ptt'] : ['ptt'];
    const effective = this.effectiveInteractionMode();
    modes.forEach(mode => {
      const button = h('button', `cm-mode-button ${effective === mode ? 'is-active' : ''}`, { type: 'button', 'aria-pressed': String(effective === mode) });
      button.textContent = mode === 'live' ? 'Always listening' : 'Press to talk';
      button.addEventListener('click', () => this.setInteractionMode(mode));
      segmented.appendChild(button);
    });
    row.append(segmented, h('span', 'cm-mode-note', {}, effective === 'live' ? 'Just talk' : 'Tap to talk'));
    return row;
  }

  private cancelAudio(): void {
    this.audioGeneration++;
    this.clearRecordingTimer();
    this.capture.cancel();
    this.audioAbort?.abort(); this.audioAbort = undefined;
    this.audioPhase = 'idle';
    this.stopPlayback();
    this.micLevel = 0;
  }

  private updateMicLevel(level: number): void {
    const bounded = Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
    if (Date.now() - this.micLevelUpdatedAt < 100 && bounded !== 0) return;
    this.micLevelUpdatedAt = Date.now();
    this.micLevel = bounded;
    if (this.root) this.render();
  }

  private stopPlayback(): void {
    if (this.spokenAudio) {
      this.spokenAudio.onended = null;
      this.spokenAudio.pause(); this.spokenAudio.removeAttribute('src');
      this.spokenAudio = undefined;
    }
    if (this.spokenUrl) URL.revokeObjectURL(this.spokenUrl);
    this.spokenUrl = undefined; this.playbackBlocked = false;
  }

  private buildRecordedAudio(): HTMLElement {
    const panel = h('div', 'cm-recorded-audio');
    const status = this.state.muted ? 'Muted — microphone off'
      : this.audioPhase === 'recording' ? `Recording — microphone on · ${this.recordingTimerLabel()} / 0:30`
      : this.audioPhase === 'requesting' ? 'Waiting for microphone permission…'
      : this.audioPhase === 'transcribing' ? 'Transcribing — microphone off'
      : 'Microphone off';
    panel.append(h('p', 'cm-mic-status', { role: 'status', 'data-state': this.state.muted ? 'muted' : this.audioPhase }, status));
    const record = h('button', `cm-record ${this.audioPhase === 'recording' ? 'is-recording' : ''}`, {
      type: 'button',
      'aria-label': this.audioPhase === 'recording' ? 'Finish recording and send' : 'Tap to talk',
      'aria-pressed': String(this.audioPhase === 'recording'),
    }) as HTMLButtonElement;
    record.append(
      this.buildMicBars(),
      h('span', 'cm-record-label', {}, this.audioPhase === 'recording' ? 'Tap when done' : 'Tap to talk'),
      h('span', 'cm-record-timer', {}, this.audioPhase === 'recording' ? this.recordingTimerLabel() : '0:30 max'),
    );
    record.disabled = this.state.muted || this.commandBusy || this.state.offline || ['requesting', 'transcribing'].includes(this.audioPhase);
    record.addEventListener('click', () => {
      if (this.audioPhase === 'recording') void this.finishRecording();
      else void this.startRecording();
    });
    panel.appendChild(record);
    if (this.audioPhase !== 'idle') {
      const discard = h('button', 'cm-button cm-discard-audio', { type: 'button' }, 'Discard recording');
      discard.addEventListener('click', () => { this.cancelAudio(); this.audioMessage = 'Recording discarded.'; this.render(); });
      panel.append(discard);
    }
    panel.append(h('p', 'cm-audio-message', { role: 'status' }, this.audioMessage));
    panel.append(h('p', 'cm-muted', {}, 'Tap once to begin and once to finish. Audio is sent only after you finish.'));
    return panel;
  }

  private recordingTimerLabel(): string {
    const elapsed = this.audioPhase === 'recording' && this.recordingStartedAt
      ? Math.min(30, Math.floor((Date.now() - this.recordingStartedAt) / 1000))
      : 0;
    return `0:${String(elapsed).padStart(2, '0')}`;
  }

  private clearRecordingTimer(): void {
    if (this.recordingTimer !== undefined) window.clearInterval(this.recordingTimer);
    this.recordingTimer = undefined;
    this.recordingStartedAt = 0;
  }
  private async startRecording(): Promise<void> {
    if (!this.audioEnabled || this.state.muted || this.state.offline || this.commandBusy || this.audioPhase !== 'idle' || !this.state.authenticated || !this.state.slackConnected) return;
    this.cancelAudio();
    const generation = this.audioGeneration;
    this.audioPhase = 'requesting'; this.audioMessage = ''; this.render();
    try {
      const started = await this.capture.start(() => { void this.finishRecording(); }, level => this.updateMicLevel(level));
      if (generation !== this.audioGeneration || !started) return;
      this.recordingStartedAt = Date.now();
      this.recordingTimer = window.setInterval(() => {
        if (this.audioPhase === 'recording') this.render();
      }, 1000);
      this.audioPhase = 'recording'; this.render();
    } catch {
      if (generation !== this.audioGeneration) return;
      this.cancelAudio();
      this.audioMessage = 'Could not start the microphone. Allow microphone access in your browser, or type your command.';
      this.render();
    }
  }

  private async finishRecording(): Promise<void> {
    if (this.audioPhase !== 'recording' || this.state.muted) return;
    const generation = this.audioGeneration;
    const auth = this.authGeneration;
    this.clearRecordingTimer();
    this.audioPhase = 'transcribing';
    this.audioAbort = new AbortController();
    try {
      const clip = this.capture.finish();
      this.render();
      if (!this.conversationId) {
        const created = await this.api.createConversation();
        if (generation !== this.audioGeneration || auth !== this.authGeneration) return;
        if (typeof created.conversation_id !== 'string' || !created.conversation_id) throw new Error('Conversation unavailable');
        this.conversationId = created.conversation_id;
        this.costs = undefined;
      }
      const transcript = await this.api.transcribe(clip, this.audioAbort.signal, this.conversationId);
      if (generation !== this.audioGeneration || auth !== this.authGeneration) return;
      await this.loadMe(false);
      if (generation !== this.audioGeneration || auth !== this.authGeneration || !this.audioEnabled) return;
      if (typeof transcript.text !== 'string' || !transcript.text.trim()) throw new Error('Empty transcript');
      this.audioPhase = 'idle';
      this.audioMessage = `Heard: ${transcript.text}`;
      this.commandDraft = transcript.text;
      await this.sendCommand(true);
    } catch (error) {
      if (generation !== this.audioGeneration || auth !== this.authGeneration) return;
      if (error instanceof ApiError && error.status === 401) { this.clearPrivateState(); return; }
      if (error instanceof ApiError && error.status === 403) { await this.accessUnavailable('Voice access is unavailable. Reconnect Slack or use text.'); return; }
      this.audioMessage = 'Could not transcribe this recording. Record again or type your command.';
    } finally {
      if (generation === this.audioGeneration) { this.audioPhase = 'idle'; this.render(); }
    }
  }

  private async speakAnswer(text: string, generation: number): Promise<void> {
    if (!text.trim() || this.state.muted || !this.audioEnabled || document.hidden) return;
    this.stopPlayback();
    this.audioAbort = new AbortController();
    try {
      const spoken = text.length > 2000 ? text.slice(0, 1850) + '. Read the full response on screen.' : text;
      const clip = await this.api.speech(spoken, this.audioAbort.signal, this.conversationId);
      if (generation !== this.audioGeneration || this.state.muted || !this.audioEnabled || document.hidden) return;
      await this.loadMe(false);
      if (generation !== this.audioGeneration || this.state.muted || !this.audioEnabled || document.hidden) return;
      this.spokenUrl = URL.createObjectURL(clip);
      this.spokenAudio = this.adapters.createAudio();
      this.spokenAudio.src = this.spokenUrl;
      this.spokenAudio.onended = () => { this.stopPlayback(); this.render(); };
      await this.playResponse();
    } catch {
      if (generation !== this.audioGeneration) return;
      this.audioMessage = 'Spoken playback is unavailable. Your response is shown below.';
      this.render();
    }
  }

  private async playResponse(): Promise<void> {
    const audio = this.spokenAudio;
    if (!audio || this.state.muted || document.hidden) return;
    try { await audio.play(); if (audio === this.spokenAudio) this.playbackBlocked = false; }
    catch { if (audio === this.spokenAudio) this.playbackBlocked = true; }
    this.render();
  }

  private buildConversations(): HTMLElement {
    const wrap = h('div', 'cm-conversations');
    const current = h('p', 'cm-current-conversation');
    current.textContent = `${this.conversationId ? 'Conversation' : 'New conversation'} · ${this.conversationStartedLabel()}`;
    if (this.resumeConfirmation) {
      const confirm = h('p', 'cm-resume-confirmation', { role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
      confirm.textContent = this.resumeConfirmation;
      wrap.append(current, confirm);
    } else {
      wrap.appendChild(current);
    }
    const controls = h('div', 'cm-conversation-controls');
    const resumable = this.conversations.filter(c => c.id !== this.conversationId);
    // A fresh user still sees the New conversation row above. Do not show an
    // empty resume popover until there is retained history to resume.
    if (resumable.length) {
      const menu = h('div', 'cm-resume-menu');
      const resume = h('button', 'cm-button cm-button-secondary cm-resume-menu-button', {
        type: 'button',
        'aria-expanded': String(this.resumeMenuOpen),
        'aria-controls': 'cm-resume-popover',
      }) as HTMLButtonElement;
      resume.append('Resume earlier ', icon(this.resumeMenuOpen ? 'chevronUp' : 'chevronDown'));
      resume.disabled = this.commandBusy || this.audioPhase !== 'idle';
      resume.addEventListener('click', () => {
        this.toggleResumeMenu();
        if (this.resumeMenuOpen) void this.refreshConversations();
      });
      menu.appendChild(resume);
      const popover = this.buildResumePopover();
      popover.hidden = !this.resumeMenuOpen;
      menu.appendChild(popover);
      if (this.resumeMenuOpen) {
        const scrim = h('button', 'cm-menu-scrim', { type: 'button', 'aria-label': 'Close saved conversations' });
        scrim.addEventListener('click', () => this.closeResumeMenu(true));
        wrap.appendChild(scrim);
      }
      controls.appendChild(menu);
    }
    if (this.conversationId) {
      const fresh = h('button', 'cm-button cm-new-conversation', { type: 'button' }) as HTMLButtonElement;
      fresh.append(icon('plus'), 'New');
      fresh.disabled = this.commandBusy || this.audioPhase !== 'idle';
      fresh.addEventListener('click', () => {
        if (this.commandBusy) return;
        this.cancelAudio();
        this.conversationId = undefined;
        this.rememberConversationSelection(undefined);
        this.conversationStartedAtOverride = undefined;
        this.resumeConfirmation = '';
        this.costs = undefined;
        this.commandAnswer = this.commandDraft = '';
        this.commandRequest = undefined;
        this.state.pending = [];
        this.render();
        void this.loadStatus();
      });
      controls.appendChild(fresh);
    }
    wrap.appendChild(controls);
    return wrap;
  }

  private conversationStartedLabel(): string {
    const conversation = this.conversations.find(item => item.id === this.conversationId);
    const ts = this.conversationStartedAtOverride ?? conversation?.created_at ?? conversation?.updated_at;
    const date = ts ? new Date(ts) : undefined;
    return date && !Number.isNaN(date.getTime()) ? `started ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'start a fresh topic';
  }

  private formatConversationDate(ts: string): string {
    const date = new Date(ts);
    return Number.isNaN(date.getTime()) ? 'unknown time' : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  private buildResumePopover(): HTMLElement {
    const resumable = this.conversations.filter(c => c.id !== this.conversationId);
    const popover = h('div', 'cm-resume-popover', { id: 'cm-resume-popover', role: 'dialog', 'aria-label': 'Saved conversations', 'aria-modal': 'true' });
    const header = h('div', 'cm-resume-popover-header');
    const eyebrow = h('p', 'cm-eyebrow', {}, `Saved conversations (${resumable.length})`);
    const close = h('button', 'cm-button cm-resume-close', { type: 'button' }, 'Close') as HTMLButtonElement;
    close.setAttribute('aria-label', 'Close saved conversations');
    close.addEventListener('click', () => this.closeResumeMenu(true));
    header.append(eyebrow, close);
    popover.appendChild(header);
    const search = h('input', 'cm-resume-search', { type: 'search', placeholder: 'Search by date', 'aria-label': 'Search saved conversations' }) as HTMLInputElement;
    search.value = this.resumeQuery;
    search.addEventListener('input', () => { this.resumeQuery = search.value; this.render(); this.focusResumeSearch(); });
    popover.appendChild(search);
    const list = h('div', 'cm-resume-list');
    const term = this.resumeQuery.trim().toLowerCase();
    const filtered = resumable.filter(conversation => {
      const label = this.formatConversationDate(conversation.updated_at).toLowerCase();
      return !term || label.includes(term);
    });
    if (filtered.length === 0) list.append(h('p', 'cm-muted', {}, 'No saved conversations match.'));
    filtered.slice(0, 20).forEach(conversation => {
      const button = h('button', 'cm-button cm-resume-conversation', { type: 'button' }) as HTMLButtonElement;
      button.textContent = this.formatConversationDate(conversation.updated_at);
      button.disabled = this.commandBusy || this.audioPhase !== 'idle';
      const startedAt = conversation.created_at ?? conversation.updated_at;
      button.addEventListener('click', () => { this.closeResumeMenu(false); void this.resumeConversation(conversation.id, startedAt); });
      list.appendChild(button);
    });
    popover.appendChild(list);
    if (filtered.length > 0) popover.appendChild(h('p', 'cm-muted cm-resume-footer', {}, `Showing ${Math.min(filtered.length, 20)} saved conversation${filtered.length === 1 ? '' : 's'}.`));
    return popover;
  }

  private toggleResumeMenu(): void {
    this.resumeMenuOpen = !this.resumeMenuOpen;
    if (!this.resumeMenuOpen) this.resumeQuery = '';
    this.setScrollLock(this.resumeMenuOpen);
    this.render();
    if (this.resumeMenuOpen) { this.focusResumeSearch(); this.positionResumeSheet(); }
  }

  private closeResumeMenu(returnFocus: boolean): void {
    if (!this.resumeMenuOpen) return;
    this.resumeMenuOpen = false;
    this.resumeQuery = '';
    this.setScrollLock(false);
    this.positionResumeSheet();
    this.render();
    if (returnFocus) this.focusResumeMenuButton();
  }

  private focusResumeMenuButton(): void {
    queueMicrotask(() => (this.root.querySelector('.cm-resume-menu-button') as HTMLButtonElement | null)?.focus());
  }

  private focusResumeSearch(): void {
    queueMicrotask(() => (this.root.querySelector('.cm-resume-search') as HTMLInputElement | null)?.focus());
  }

  private setScrollLock(locked: boolean): void {
    document.body.classList.toggle('cm-resume-locked', locked);
  }

  private async accessUnavailable(message: string): Promise<void> {
    this.clearPrivateState();
    const generation = this.authGeneration;
    await this.loadMe(false);
    if (generation !== this.authGeneration || !this.state.authenticated) return;
    this.commandAnswer = message;
    this.render();
  }

  private async resumeConversation(id: string, updatedAt: string): Promise<void> {
    if (this.commandBusy || !this.state.authenticated || !this.state.textEnabled) return;
    this.cancelAudio();
    const previousConversationId = this.conversationId;
    const previousStartedAtOverride = this.conversationStartedAtOverride;
    this.conversationStartedAtOverride = updatedAt;
    const label = this.formatConversationDate(updatedAt);
    this.resumeConfirmation = `Resumed conversation from ${label}.`;
    this.commandAnswer = 'Previous confirmations are no longer valid; ask again to prepare a fresh message.';
    this.conversationId = id;
    this.costs = undefined;
    this.commandDraft = '';
    this.commandRequest = undefined;
    this.state.pending = [];
    this.render();
    this.focusResumeMenuButton();
    const generation = this.authGeneration;
    this.commandBusy = true;
    this.render();
    try {
      const response = await this.api.resumeConversation(id);
      if (generation !== this.authGeneration) return;
      this.conversationId = response.conversation_id;
      this.rememberConversationSelection(this.conversationId);
      this.costs = undefined;
    } catch (err) {
      if (generation !== this.authGeneration) return;
      if (err instanceof ApiError && err.status === 401) { this.clearPrivateState(); return; }
      if (err instanceof ApiError && [403, 404, 409].includes(err.status)) { await this.accessUnavailable('That conversation expired or is no longer available. Start a new conversation.'); return; }
      this.conversationId = previousConversationId;
      this.conversationStartedAtOverride = previousStartedAtOverride;
      this.resumeConfirmation = '';
      this.commandAnswer = 'The conversation could not be resumed. Refresh and try again.';
    } finally {
      if (generation === this.authGeneration) { this.commandBusy = false; this.render(); await this.loadStatus(); }
    }
  }

  private async refreshConversations(): Promise<void> {
    const generation = this.authGeneration;
    try {
      const response = await this.api.getConversations();
      if (generation !== this.authGeneration || !this.state.authenticated) return;
      this.conversations = Array.isArray(response.conversations) ? response.conversations : [];
      this.restoreConversationSelection();
      this.render();
      if (this.resumeMenuOpen) {
        this.focusResumeSearch();
        this.positionResumeSheet();
      }
    } catch {
      // The retained list remains usable. The normal status path owns errors.
    }
  }

  private rememberConversationSelection(id: string | undefined): void {
    try {
      if (id) window.localStorage.setItem('cm-voice-conversation-id', id);
      else window.localStorage.removeItem('cm-voice-conversation-id');
    } catch { /* unavailable */ }
  }

  private restoreConversationSelection(): void {
    if (this.conversationId) return;
    let saved: string | null = null;
    try { saved = window.localStorage.getItem('cm-voice-conversation-id'); } catch { /* unavailable */ }
    const conversation = saved ? this.conversations.find(item => item.id === saved) : undefined;
    if (!conversation) return;
    this.conversationId = conversation.id;
    this.conversationStartedAtOverride = conversation.created_at ?? conversation.updated_at;
    this.resumeConfirmation = 'Restored your last conversation.';
  }

  private buildCommandForm(): HTMLElement {
    const wrap = h('div', 'cm-text-commands');
    if (!this.state.textEnabled) {
      wrap.textContent = 'Text commands are not enabled for your account.';
      return wrap;
    }
    const form = h('form', 'cm-command-form') as HTMLFormElement;
    const label = h('label', 'cm-sr-only', { for: 'cm-command' });
    label.textContent = 'Type a Slack command';
    const input = h('textarea', 'cm-command-input', { id: 'cm-command', rows: '1', maxlength: '4000', placeholder: 'Or type a Slack command…' }) as HTMLTextAreaElement;
    input.value = this.commandDraft;
    input.disabled = this.commandBusy || this.audioPhase !== 'idle';
    input.addEventListener('input', () => { this.commandDraft = input.value; });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    const submit = h('button', 'cm-button cm-button-primary cm-command-send', { type: 'submit', 'aria-label': this.commandBusy ? 'Sending command' : 'Send command' }) as HTMLButtonElement;
    submit.appendChild(icon('send'));
    submit.disabled = this.commandBusy || this.state.offline || this.audioPhase !== 'idle';
    form.append(label, input, submit);
    form.addEventListener('submit', (event) => { event.preventDefault(); void this.sendCommand(); });
    wrap.append(form);
    return wrap;
  }

  private async sendCommand(spoken = false) {
    const audioGeneration = this.audioGeneration;
    const draft = this.commandDraft.trim();
    if (!draft || draft.length > 4000) return;
    const selected = this.state.delegations.find(item => item.id === this.selectedDelegationId);
    const text = draft;
    if (this.commandBusy || this.audioPhase !== 'idle' || !this.state.textEnabled || !this.state.authenticated || !this.state.slackConnected) return;
    const generation = this.authGeneration;
    if (this.commandRequest?.text !== text || this.commandRequest?.selected_delegation_id !== selected?.id) {
      this.commandRequest = {
        text,
        selected_delegation_id: selected?.id,
        request_id: crypto.randomUUID(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      };
    }
    const request = this.commandRequest!;
    this.commandBusy = true;
    this.render();
    try {
      const result = await this.api.command({
        ...request,
        conversation_id: this.conversationId,
      });
      if (generation !== this.authGeneration) return;
      this.conversationId = result.conversation_id;
      this.rememberConversationSelection(this.conversationId);
      this.commandAnswer = result.text;
      if (spoken && audioGeneration === this.audioGeneration && !this.state.muted) await this.speakAnswer(result.text, audioGeneration);
      this.commandDraft = '';
      this.commandRequest = undefined;
      if (result.confirmation) this.state.pending = [...this.state.pending.filter(a => a.id !== result.confirmation!.id), result.confirmation];
      if (result.delegation) this.state.delegations = [...this.state.delegations.filter(d => d.id !== result.delegation!.id), result.delegation];
    } catch (err) {
      if (generation !== this.authGeneration) return;
      if (err instanceof ApiError && err.status === 401) { this.clearPrivateState(); return; }
      if (err instanceof ApiError && err.status === 403) { await this.accessUnavailable('That conversation or Slack access is no longer available. Start a new conversation or reconnect Slack.'); return; }
      this.commandAnswer = 'No confirmed result was received. Retry the same command to check that request; do not assume a message was sent.';
    } finally {
      if (generation === this.authGeneration) { this.commandBusy = false; this.render(); await this.loadStatus(); }
    }
  }

  private async textAction(action: ActionConfirmation, cancel: boolean) {
    if (action.epoch === undefined || action.busy || action.result || isExpired(action.expires_at)) return;
    const generation = this.authGeneration;
    action.busy = true;
    this.render();
    try {
      const response = cancel
        ? await this.api.cancelAction(action.id, action.manifest_hash, action.epoch)
        : await this.api.confirmAction(action.id, action.manifest_hash, action.epoch);
      if (generation === this.authGeneration) action.result = response.action;
    } catch (err) {
      if (generation !== this.authGeneration) return;
      action.result = { id: action.id, status: err instanceof ApiError && [400, 403, 404, 422].includes(err.status) ? 'REJECTED' : 'UNKNOWN' };
      if (err instanceof ApiError && err.status === 401) { this.clearPrivateState(); return; }
      if (err instanceof ApiError && err.status === 403) { await this.accessUnavailable('This confirmation is no longer available. Request a fresh preview.'); return; }
    } finally {
      action.busy = false;
      if (generation === this.authGeneration) { this.render(); await this.loadStatus(); }
    }
  }

  private buildCallPanel(): HTMLElement {
    const wrap = h('div', 'cm-call cm-workspace');
    wrap.appendChild(this.buildConversations());
    const voiceCard = h('div', 'cm-voice-card');
    this.controlsEl = this.buildControls();
    this.captionsEl = this.buildCaptions();
    this.pendingEl = this.buildPending();
    voiceCard.append(this.controlsEl, this.buildModeControl(), this.buildCommandForm());
    wrap.append(voiceCard, this.captionsEl, this.pendingEl);
    this.delegationsEl = this.buildDelegations();
    wrap.appendChild(this.delegationsEl);
    return wrap;
  }

  private buildControls(): HTMLElement {
    const wrap = h('div', 'cm-controls');
    wrap.appendChild(this.buildVoiceStatus());
    const secondary = h('div', 'cm-live-controls');
    const endBtn = h('button', 'cm-button cm-button-secondary cm-end', { type: 'button' });
    endBtn.textContent = 'End';
    endBtn.addEventListener('click', () => this.endCall());
    const resumeBtn = h('button', 'cm-button cm-button-secondary cm-audio-resume', { type: 'button' });
    resumeBtn.textContent = 'Resume audio';
    resumeBtn.addEventListener('click', () => this.resumeAudio());
    secondary.append(endBtn, resumeBtn);
    wrap.appendChild(secondary);
    return wrap;
  }

  private updateControls() {
    if (!this.controlsEl) return;
    const updated = this.buildControls();
    this.controlsEl.replaceWith(updated);
    this.controlsEl = updated;
  }

  private buildCaptions(): HTMLElement {
    const wrap = h('div', 'cm-captions', { 'aria-live': 'polite', 'aria-atomic': 'false' });
    if (this.state.captions.length === 0) {
      const p = h('p', 'cm-placeholder');
      p.textContent = 'Ask about messages, channels, or people. Posting to Slack always asks you to confirm first.';
      wrap.appendChild(p);
      if (this.state.textEnabled) {
        const suggestions = h('div', 'cm-suggestions');
        ["What was Eric's last message to me?", 'Anything new in #design today?'].forEach(text => {
          const chip = h('button', 'cm-suggestion', { type: 'button' });
          chip.textContent = `“${text}”`;
          chip.addEventListener('click', () => { this.commandDraft = text; void this.sendCommand(); });
          suggestions.appendChild(chip);
        });
        wrap.appendChild(suggestions);
      }
    } else {
      this.state.captions.forEach((c) => {
        wrap.appendChild(this.buildCaptionLine(c));
      });
    }
    if (this.commandAnswer) wrap.appendChild(this.buildAnswerCard(this.commandAnswer));
    return wrap;
  }

  private buildCaptionLine(c: import('./types').TranscriptSegment): HTMLElement {
    const line = h('div', `cm-caption cm-caption-${c.speaker}`, { 'data-speaker': c.speaker, 'data-start': String(c.start_ms) });
    const text = h('span', c.speaker === 'assistant' ? 'cm-markdown' : undefined);
    if (c.speaker === 'assistant') text.appendChild(renderMarkdown(c.text));
    else text.textContent = c.text;
    line.append(text);
    return line;
  }

  private buildAnswerCard(answer: string): HTMLElement {
    const wrap = h('article', 'cm-command-answer cm-assistant-turn', { role: 'status' });
    const lines = answer.trim().split(/\r?\n/);
    const headingMatch = lines[0]?.match(/^\*\*([^*\n]{1,160})\*\*\s*$/);
    let referenceMeta = '';
    if (headingMatch) {
      const heading = headingMatch[1].trim();
      if (/^.+\s—\s.+\s—\s(?:#[\w-]+|DM)$/u.test(heading)) referenceMeta = heading;
      if (referenceMeta) lines.shift();
    }
    const caveatIndex = lines.findIndex(line => /^(coverage caveat\s*:|newest match from Slack search)/i.test(line.trim()));
    const content = caveatIndex >= 0 ? lines.slice(0, caveatIndex) : lines;
    const caveat = caveatIndex >= 0 ? lines.slice(caveatIndex).join('\n').replace(/^coverage caveat\s*:\s*/i, '').trim() : '';
    const card = h('div', 'cm-slack-message');
    if (referenceMeta) card.appendChild(h('p', 'cm-slack-meta', {}, referenceMeta));
    const rendered = renderMarkdown(content.join('\n'));
    const hasRenderedContent = rendered.childNodes.length > 0;
    card.appendChild(rendered);
    if (referenceMeta || hasRenderedContent) wrap.appendChild(card);
    if (caveat) {
      const caveatElement = h('div', 'cm-answer-caveat');
      caveatElement.appendChild(renderMarkdown(`**Coverage caveat:** ${caveat}`));
      wrap.appendChild(caveatElement);
    }
    return wrap;
  }

  private updateCaptions() {
    if (!this.captionsEl) return;
    const updated = this.buildCaptions();
    this.captionsEl.replaceWith(updated);
    this.captionsEl = updated;
  }

  private buildPending(): HTMLElement {
    const wrap = h('div', 'cm-pending', { 'aria-live': 'polite' });
    if (this.state.pending.length === 0) return wrap;
    const heading = h('h2');
    heading.textContent = 'Pending actions';
    wrap.appendChild(heading);
    this.state.pending.forEach((a) => wrap.appendChild(this.buildActionCard(a)));
    return wrap;
  }

  private buildActionCard(a: ActionConfirmation): HTMLElement {
    const card = h('div', 'cm-confirm-card', { 'data-action-id': a.id });
    const dest = h('div', 'cm-confirm-dest');
    const thread = a.thread_ts ? ' (thread)' : '';
    dest.textContent = `To #${a.channel_name}${thread}`;
    const body = h('p');
    body.textContent = a.text;
    const meta = h('div', 'cm-confirm-meta');
    meta.textContent = `Expires: ${new Date(a.expires_at).toLocaleString()}`;
    const status = h('div', 'cm-confirm-status');
    status.textContent = a.result ? `Status: ${a.result.status}` : '';
    const confirm = h('button', 'cm-button cm-button-primary cm-confirm-ok', { type: 'button' });
    confirm.textContent = a.busy ? 'Confirming…' : 'Confirm';
    confirm.addEventListener('click', () => this.confirmAction(a));
    const cancel = h('button', 'cm-button cm-button-secondary cm-confirm-cancel', { type: 'button' });
    cancel.textContent = a.busy ? 'Canceling…' : 'Cancel';
    cancel.addEventListener('click', () => this.cancelAction(a));
    card.append(dest, body, meta, status, confirm, cancel);
    this.updateActionCard(card, a);
    return card;
  }

  private updateActionCard(card: HTMLElement, a: ActionConfirmation): void {
    const status = card.querySelector('.cm-confirm-status') as HTMLElement | null;
    if (status) status.textContent = a.result?.status === 'REJECTED' ? 'Confirmation rejected. Request a fresh preview; nothing was retried.' : a.result?.status === 'UNKNOWN' ? 'Outcome unknown. Check the Slack thread before trying again.' : a.result ? `Status: ${a.result.status}` : '';
    let link = card.querySelector<HTMLAnchorElement>('.cm-action-permalink');
    if (a.result?.status === 'SENT' && isSlackPermalink(a.result.permalink)) {
      if (!link) { link = h('a', 'cm-action-permalink') as HTMLAnchorElement; card.appendChild(link); }
      link.href = a.result.permalink!; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.textContent = 'Open message in Slack';
    } else { link?.remove(); }
    const confirm = card.querySelector('.cm-confirm-ok') as HTMLButtonElement | null;
    const cancel = card.querySelector('.cm-confirm-cancel') as HTMLButtonElement | null;
    const expired = isExpired(a.expires_at);
    if (confirm) {
      confirm.disabled = !!a.busy || !!a.result || expired || !this.state.writeEnabled;
      confirm.textContent = a.busy ? 'Confirming…' : 'Confirm';
    }
    if (cancel) {
      cancel.disabled = !!a.busy || !!a.result || expired;
      cancel.textContent = a.busy ? 'Canceling…' : 'Cancel';
    }
  }

  private updatePending() {
    if (!this.pendingEl) return;
    const heading = this.pendingEl.querySelector('h2');
    if (this.state.pending.length > 0 && !heading) {
      const h2 = h('h2');
      h2.textContent = 'Pending actions';
      this.pendingEl.prepend(h2);
    } else if (this.state.pending.length === 0 && heading) {
      this.pendingEl.textContent = '';
      return;
    }
    if (this.state.pending.length === 0) return;
    const existing = new Map<string, HTMLElement>();
    for (const child of Array.from(this.pendingEl.children)) {
      const id = (child as HTMLElement).dataset['actionId'];
      if (id) existing.set(id, child as HTMLElement);
    }
    for (const a of this.state.pending) {
      const card = existing.get(a.id);
      if (card) {
        this.updateActionCard(card, a);
      } else {
        this.pendingEl.appendChild(this.buildActionCard(a));
      }
    }
    for (const [id, el] of existing) {
      if (!this.state.pending.some((a) => a.id === id)) el.remove();
    }
  }

  private buildDelegations(): HTMLElement {
    const wrap = h('section', 'cm-delegations cm-agent-threads', { 'aria-live': 'polite' });
    if (this.state.delegations.length === 0) return wrap;
    const heading = h('h2');
    heading.textContent = `Agent threads (${this.state.delegations.length})`;
    wrap.appendChild(heading);
    const selected = this.state.delegations.find(item => item.id === this.selectedDelegationId);
    if (selected) wrap.appendChild(h('p', 'cm-agent-context', {}, `Controlling this Claude thread: #${selected.channel_id} · ${selected.status}`));
    this.state.delegations.forEach((d) => wrap.appendChild(this.buildDelegationRow(d)));
    return wrap;
  }

  private buildDelegationRow(d: import('./types').Delegation): HTMLElement {
    const row = h('article', 'cm-delegation cm-agent-thread', { 'data-delegation-id': d.id });
    const select = h('button', 'cm-button cm-agent-select', { type: 'button', 'aria-pressed': String(this.selectedDelegationId === d.id) }) as HTMLButtonElement;
    select.textContent = this.selectedDelegationId === d.id ? 'Selected' : 'Use this thread';
    select.addEventListener('click', () => {
      this.selectedDelegationId = this.selectedDelegationId === d.id ? undefined : d.id;
      this.render();
    });
    const status = h('span', 'cm-delegation-status');
    status.textContent = d.status;
    const safe = isSlackPermalink(d.permalink);
    const link = h('a', 'cm-delegation-link', safe ? { href: d.permalink!, target: '_blank', rel: 'noopener' } : {});
    link.textContent = safe ? 'Open thread' : `#${d.channel_id}`;
    const meta = h('div', 'cm-agent-meta');
    meta.append(status, h('span', undefined, {}, `#${d.channel_id}`));
    if (d.updated_at) meta.appendChild(h('time', undefined, { datetime: d.updated_at }, new Date(d.updated_at).toLocaleString()));
    row.append(meta, select, link);
    return row;
  }

  private updateDelegationRow(row: HTMLElement, d: import('./types').Delegation): void {
    const status = row.querySelector('.cm-delegation-status') as HTMLElement | null;
    if (status) status.textContent = d.status;
    const link = row.querySelector('.cm-delegation-link') as HTMLAnchorElement | null;
    if (!link) return;
    if (isSlackPermalink(d.permalink)) {
      link.href = d.permalink!;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener');
      link.textContent = 'Open thread';
    } else {
      link.removeAttribute('href');
      link.removeAttribute('target');
      link.removeAttribute('rel');
      link.textContent = `#${d.channel_id}`;
    }
  }

  private updateDelegations() {
    if (!this.delegationsEl) return;
    const updated = this.buildDelegations();
    this.delegationsEl.replaceWith(updated);
    this.delegationsEl = updated;
  }

  private buildCost(): HTMLElement {
    const wrap = h('div', 'cm-cost');
    const summary = h('div', 'cm-cost-summary');
    summary.append(h('span', undefined, {}, 'Estimated AI cost'));
    const toggle = h('button', 'cm-link-button cm-cost-toggle', { type: 'button', 'aria-expanded': String(this.costDetailsOpen) });
    toggle.textContent = this.costDetailsOpen ? 'Hide details' : 'Details';
    toggle.addEventListener('click', () => { this.costDetailsOpen = !this.costDetailsOpen; this.updateCost(); });
    summary.appendChild(toggle);
    const amounts = h('div', 'cm-cost-amounts');
    [
      ['Conversation', this.costs?.periods.conversation],
      ['Week', this.costs?.periods.week_to_date],
      ['Month', this.costs?.periods.month_to_date],
    ].forEach(([label, period]) => amounts.appendChild(h('span', undefined, {}, `${label}: ${this.costAmount(period as CostPeriod | undefined)}${(period as CostPeriod | undefined)?.partial ? ' (partial)' : ''}`)));
    summary.appendChild(amounts);
    wrap.appendChild(summary);
    const detail = h('div', 'cm-cost-details');
    detail.hidden = !this.costDetailsOpen;
    detail.append(h('h2', undefined, {}, 'Estimated AI costs'));
    const rows: Array<[string, CostPeriod | null | undefined]> = [
      ['This conversation', this.costs?.periods.conversation],
      ['Week to date', this.costs?.periods.week_to_date],
      ['Month to date', this.costs?.periods.month_to_date],
    ];
    for (const [label, period] of rows) {
      const amount = this.costAmount(period);
      detail.append(h('p', 'cm-cost-period', {}, `${label}: ${amount}${period?.partial ? ' (partial)' : ''}`));
    }
    const reasons = [...new Set(rows.flatMap(([, period]) => period?.reasons ?? []))];
    if (rows.some(([, period]) => period?.partial)) detail.append(h('p', 'cm-muted', {}, 'Partial amounts are known subtotals. Missing costs are not counted as zero.'));
    if (reasons.length) detail.append(h('p', 'cm-muted cm-cost-note', {}, reasons.join(' ')));
    detail.append(h('p', 'cm-muted', {}, `Your CM Voice usage across devices. USD estimates, not an invoice. Week starts Monday${this.costs?.timezone ? `; timezone: ${this.costs.timezone}` : ''}. Excludes cloud hosting and subscriptions.`));
    const commands = h('span', 'cm-command-usage', {}, this.commandUsageText());
    detail.append(commands);
    wrap.append(detail);
    return wrap;
  }

  private costAmount(period: CostPeriod | null | undefined): string {
    if (!period || !Number.isFinite(period.known_estimated_usd)) return 'Unavailable';
    if (period.known_estimated_usd > 0 && period.known_estimated_usd < 0.0001) return 'Less than $0.0001';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(period.known_estimated_usd);
  }

  private updateCost() {
    if (!this.costEl) return;
    const updated = this.buildCost();
    this.costEl.replaceWith(updated);
    this.costEl = updated;
  }

  private commandUsageText(): string {
    return this.commandUsage
      ? `Today’s commands: ${this.commandUsage.input.toLocaleString()} input / ${this.commandUsage.output.toLocaleString()} output tokens`
      : 'Command usage unavailable';
  }

  private buildSignIn(): HTMLElement {
    const wrap = h('div', 'cm-auth');
    const p = h('p');
    p.textContent = 'Sign in with Slack to continue.';
    wrap.appendChild(p);
    const btn = h('button', 'cm-button cm-button-primary', { type: 'button' });
    btn.textContent = 'Sign in with Slack';
    btn.addEventListener('click', () => {
      this.adapters.location.href = '/auth/slack/login';
    });
    wrap.appendChild(btn);
    return wrap;
  }

  private buildConnectSlack(): HTMLElement {
    const wrap = h('div', 'cm-auth');
    const p = h('p');
    p.textContent = 'Connect your Slack workspace to enable voice actions.';
    wrap.appendChild(p);
    const btn = h('button', 'cm-button cm-button-primary', { type: 'button' });
    btn.textContent = 'Connect Slack';
    btn.addEventListener('click', () => {
      this.adapters.location.href = '/auth/slack/connect';
    });
    wrap.appendChild(btn);
    return wrap;
  }

  private buildError(): HTMLElement {
    const wrap = h('div', 'cm-error', { role: 'alert' });
    const p = h('p');
    p.textContent = GUIDANCE[this.state.error!.code] || this.state.error!.message;
    wrap.appendChild(p);
    const dismiss = h('button', 'cm-button cm-button-secondary', { type: 'button' });
    dismiss.textContent = 'Dismiss';
    dismiss.addEventListener('click', () => {
      this.state.error = undefined;
      this.render();
    });
    wrap.appendChild(dismiss);
    return wrap;
  }

  private buildOfflineBanner(): HTMLElement {
    const wrap = h('div', 'cm-offline', { role: 'status' });
    wrap.textContent = 'You are offline. CM Voice is unavailable until the network returns.';
    return wrap;
  }

  private buildUnsupported(): HTMLElement {
    const wrap = h('div', 'cm-unsupported');
    wrap.textContent =
      'This browser does not support the WebRTC voice features CM Voice needs. Try Safari, Chrome, Edge, or a Home Screen/PWA install.';
    return wrap;
  }

  private buildInstall(): HTMLElement {
    const wrap = h('div', 'cm-install');
    if (this.isStandalone()) return wrap;
    const btn = h('button', 'cm-link-button cm-install-trigger', {
      type: 'button',
      'aria-expanded': String(this.installHelpOpen),
      'aria-controls': 'cm-install-help',
    });
    btn.textContent = 'Add to phone';
    btn.addEventListener('click', () => { void this.addToPhone(); });
    wrap.appendChild(btn);
    if (this.installHelpOpen) {
      const p = h('p', 'cm-install-hint', { id: 'cm-install-help', role: 'status' });
      if (this.isIosBrowser()) {
        p.textContent = 'On iPhone or iPad: tap Share, then Add to Home Screen.';
      } else if (this.isAndroidBrowser()) {
        p.textContent = 'Open the browser menu, then tap Install app or Add to Home screen.';
      } else {
        p.textContent = 'Open this same CM Voice link on your phone, then tap Add to phone there.';
      }
      wrap.appendChild(p);
    }
    return wrap;
  }

  private async addToPhone(): Promise<void> {
    const prompt = this.installEvent;
    if (prompt) {
      this.installEvent = null;
      try {
        await prompt.prompt();
        await prompt.userChoice;
      } catch {
        this.installHelpOpen = true;
      }
      this.render();
      return;
    }
    this.installHelpOpen = !this.installHelpOpen;
    this.render();
  }

  private isStandalone(): boolean {
    const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    const displayStandalone = typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches;
    return iosStandalone || displayStandalone;
  }

  private isIosBrowser(): boolean {
    const ua = (window.navigator.userAgent || '').toLowerCase();
    return /iphone|ipad|ipod/.test(ua)
      || (/macintosh/.test(ua) && window.navigator.maxTouchPoints > 1);
  }

  private isAndroidBrowser(): boolean {
    return /android/.test((window.navigator.userAgent || '').toLowerCase());
  }

  private async startCall(takeover = false) {
    if (!this.canArmLive() || this.effectiveInteractionMode() !== 'live' || this.state.muted) return;
    this.cancelAudio();
    this.takeoverOnWake = takeover;
    await this.handsFree.start();
  }

  private async startPaidCall(stream: MediaStream) {
    if (!this.supportsVoice || !this.me?.authenticated || !this.me.slack_connected || !this.state.voiceEnabled || this.state.offline || document.hidden || !['idle', 'closed', 'error'].includes(this.state.status)) {
      stream.getTracks().forEach(track => track.stop());
      await this.handsFree.stop();
      return;
    }
    const takeover = this.takeoverOnWake;
    this.takeoverOnWake = false;
    this.state.error = undefined;
    this.state.takeoverOffered = false;
    this.state.audioBlocked = false;
    const generation = this.authGeneration;
    this.session = new VoiceSession({
      api: this.api,
      adapters: this.adapters,
      inputStream: stream,
      conversationId: this.conversationId,
      selectedDelegationId: this.selectedDelegationId,
      onChange: (s) => { if (generation === this.authGeneration) this.onSessionChange(s); },
      onAudioBlocked: () => {
        this.state.audioBlocked = true;
        this.render();
      },
    });
    this.render();
    try {
      await this.session.start(takeover);
    } catch {
      // errors flow through onChange
    }
  }

  private setMuted(muted: boolean) {
    if (this.effectiveInteractionMode() === 'ptt') {
      this.state.muted = muted;
      if (muted) {
        this.cancelAudio();
        this.audioMessage = 'Muted. Any unsent recording was discarded.';
      } else {
        this.audioMessage = 'Microphone remains off until you tap to talk.';
      }
      this.render();
      return;
    }
    if (muted) void this.handsFree.mute();
    else void this.handsFree.start();
  }

  private canArmLive(): boolean {
    return this.supportsVoice
      && this.state.authenticated
      && this.state.slackConnected
      && this.state.voiceEnabled
      && !this.state.offline;
  }

  private async endCall() {
    await this.handsFree.stop();
    this.session = undefined;
    this.state.audioBlocked = false;
    this.state.captions = [];
    this.render();
    await this.loadStatus();
  }

  private async confirmAction(a: ActionConfirmation) {
    if (!this.state.writeEnabled) return;
    if (!this.session) { await this.textAction(a, false); return; }
    try {
      await this.session?.confirm(a);
    } catch {
      // VoiceSession records the failed result in state
    }
  }

  private async cancelAction(a: ActionConfirmation) {
    if (!this.session) { await this.textAction(a, true); return; }
    try {
      await this.session?.cancel(a);
    } catch {
      // VoiceSession records the failed result in state
    }
  }

  private resumeAudio() {
    this.session?.resumeAudio();
    this.state.audioBlocked = false;
    this.render();
  }

  private clearPrivateState() {
    this.authGeneration++;
    this.cancelAudio();
    void this.handsFree.stop();
    this.audioEnabled = false;
    this.state.muted = false;
    this.audioMessage = '';
    this.commandUsage = undefined;
    this.costs = undefined;
    this.commandAnswer = this.commandDraft = "";
    this.commandBusy = false;
    this.commandRequest = undefined;
    this.selectedDelegationId = undefined;
    this.resumeConfirmation = '';
    this.conversationStartedAtOverride = undefined;
    this.setScrollLock(false);
    const previous = this.session;
    this.session = undefined;
    this.conversationId = undefined;
    this.rememberConversationSelection(undefined);
    this.conversations = [];
    this.me = undefined;
    this.installEvent = null;
    this.state = {
      status: 'idle',
      authenticated: false,
      slackConnected: false,
      voiceEnabled: false,
      textEnabled: false,
      writeEnabled: false,
      captions: [],
      pending: [],
      delegations: [],
      usageSeconds: 0,
      costUsd: null,
      costFinal: false,
      muted: false,
      offline: !this.adapters.navigator.onLine,
      audioBlocked: false,
      takeoverOffered: false,
      error: undefined,
    };
    this.render();
    previous?.dispose();
  }

  private async logout() {
    this.clearPrivateState();
    try {
      await this.api.logout();
      this.api.setCsrf('');
    } catch {
      this.state.error = { code: 'network', message: 'Logout could not be confirmed. Please retry logout when connected.', recoverable: true };
      this.render();
    }
  }

  private onSessionChange(s: VoiceState) {
    if (s.error?.code === 'auth') {
      this.clearPrivateState();
      return;
    }
    const usageChanged = s.usageSeconds !== this.state.usageSeconds || s.conversationId !== this.conversationId;
    const liveDelegationIds = new Set(s.delegations.map(item => item.id));
    const delegations = s.delegations.length
      ? [...s.delegations, ...this.state.delegations.filter(item => !liveDelegationIds.has(item.id))]
      : this.state.delegations;
    this.state = {
      ...this.state,
      status: s.status,
      muted: this.handsFreeMode === 'muted' || s.muted,
      captions: s.captions,
      pending: s.pending,
      delegations,
      usageSeconds: s.usageSeconds,
      costUsd: s.costUsd,
      costFinal: s.costFinal,
      error: s.error,
      conversationId: s.conversationId ?? this.state.conversationId,
    };
    if (s.conversationId) {
      this.conversationId = s.conversationId;
      this.rememberConversationSelection(s.conversationId);
    }
    if (s.status === 'closed' || s.status === 'error') {
      this.session = undefined;
      this.handsFree.closed(s.closeReason, s.costFinal);
      this.state.audioBlocked = false;
      this.state.takeoverOffered = s.error?.code === 'conflict';
      this.loadStatus();
    }
    if (s.status === 'ready') this.handsFree.ready();
    if (s.status === 'error') {
      this.state.error = s.error;
    }
    if (usageChanged && s.status === 'ready' && !this.costRefreshBusy && Date.now() - this.costRefreshAt >= 15000) {
      this.costRefreshAt = Date.now();
      this.costRefreshBusy = true;
      void this.loadStatus().finally(() => { this.costRefreshBusy = false; });
    }
    this.render();
  }
}
